import { createContext, useContext, useState, ReactNode } from 'react';
import type { IconType } from 'react-icons';

/** Opção de um dropdown (ex.: Exportar > Excel, PDF) */
export interface BreadcrumbDropdownOption {
  label: string;
  onClick: () => void;
}

/** Uma ação em um slot do breadcrumb (Ação Primária, Secundária, Imprimir/Exportar ou Histórico) */
export interface BreadcrumbAction {
  label: string;
  onClick: () => void;
  href?: string;
  /** Ex.: 'primary' para botão de destaque (ex.: Cadastrar) no breadcrumb */
  variant?: 'primary' | 'outline';
  /** Ícone customizado; se omitido, usa o padrão do slot */
  icon?: IconType;
  /** Se definido, renderiza dropdown com estas opções (ex.: Exportar > Excel, PDF) */
  dropdownOptions?: BreadcrumbDropdownOption[];
}

/** Opções de exibição do cabeçalho unificado (AppHeader + barra de ações) */
export interface BreadcrumbHeaderOptions {
  /** Exibir trilha de navegação; omitido = regras automáticas por rota */
  showBreadcrumb?: boolean;
  /** Título customizado da página */
  title?: string;
}

/** Ordem fixa: até 5 botões na barra central de ações */
export interface BreadcrumbActionsConfig {
  primary?: BreadcrumbAction;
  secondary?: BreadcrumbAction;
  tertiary?: BreadcrumbAction;
  print?: BreadcrumbAction;
  history?: BreadcrumbAction;
  header?: BreadcrumbHeaderOptions;
}

interface BreadcrumbActionsContextType {
  actions: BreadcrumbActionsConfig;
  setActions: (actions: BreadcrumbActionsConfig) => void;
}

export const BreadcrumbActionsContext = createContext<BreadcrumbActionsContextType | undefined>(undefined);

export function BreadcrumbActionsProvider({ children }: { children: ReactNode }) {
  const [actions, setActions] = useState<BreadcrumbActionsConfig>({});

  return (
    <BreadcrumbActionsContext.Provider value={{ actions, setActions }}>
      {children}
    </BreadcrumbActionsContext.Provider>
  );
}

export function useBreadcrumbActions() {
  const context = useContext(BreadcrumbActionsContext);
  if (!context) {
    throw new Error('useBreadcrumbActions must be used within BreadcrumbActionsProvider');
  }
  return context;
}
